import Avatar from 'boring-avatars'
import { useEffect, useState } from 'react'

import { API } from '@/api'
import { GetTeamInfoRes } from '@/api/type-response'
import { Loading, Trans } from '@/components'
import { useTeamStore } from '@/stores/team'

export function TeamInfoHeader() {
  const [teamInfo, setTeamInfo] = useState<GetTeamInfoRes['team'] | null>(null)
  const [loading, setLoading] = useState(true)
  const teamStore = useTeamStore()
  const setMemberStat = useTeamStore((state) => state.setMemberStat)

  useEffect(() => {
    const fetchTeamInfo = async () => {
      try {
        const teamResID = teamStore.teamTokenData?.teamResID
        if (!teamResID) {
          console.error('No team res ID found')
          return
        }

        const teamInfoRes = await API.getTeamInfo({ res_id: teamResID })
        setTeamInfo(teamInfoRes.team)
        setMemberStat(teamInfoRes.team.memberStat)
      } catch (error) {
        console.error('Failed to fetch team info:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTeamInfo()
  }, [teamStore.teamTokenData?.teamResID])

  if (loading) {
    return (
      <div className="bg-panel-white rounded-[32px] p-8 flex items-center justify-center min-h-[180px]">
        <Loading />
      </div>
    )
  }

  if (!teamInfo) {
    return (
      <div className="bg-panel-white rounded-[32px] p-8 flex items-center justify-center min-h-[180px]">
        <div className="text-secondary">
          <Trans i18nKey="teamHome.teamInfo.loadError">Failed to load team information</Trans>
        </div>
      </div>
    )
  }

  const { team, memberStat } = teamInfo

  return (
    <div className="bg-panel-white rounded-[32px] px-8 py-6">
      <div className="flex gap-6 items-start">
        {/* Team Logo */}
        <Avatar
          size={64}
          name={team.name}
          variant="beam"
          colors={['#525866', '#7B8394', '#A0A8B8']}
        />

        {/* Team Info */}
        <div className="flex-1 flex flex-col gap-4">
          <div>
            <h1 className="h6 mb-1">{team.name}</h1>
            <p className="t-xs text-secondary">{team.introduction}</p>
          </div>

          <div className="flex flex-wrap gap-x-6">
            <div className="flex gap-2 p-2 rounded-lg bg-neutral-100">
              <span className="t-xs text-secondary">
                <Trans i18nKey="teamHome.teamInfo.teamId">Team ID</Trans>
              </span>
              <span className="t-xs">{team.desc.resID}</span>
            </div>
            <div className="flex gap-2 p-2 rounded-lg bg-neutral-100">
              <span className="t-xs text-secondary">
                <Trans i18nKey="teamHome.teamInfo.teamSize">Team Size</Trans>
              </span>
              <span className="t-xs">
                {memberStat?.membersCount ?? 0}/{memberStat?.membersNumLimit ?? 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
